import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, FileSpreadsheet, Lock, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const EXPORT_COLUMNS = [
  { id: 'nome_razao_social', label: 'Nome/Razão Social', required: true },
  { id: 'cpf_cnpj', label: 'CPF/CNPJ', required: true },
  { id: 'tipo_pessoa', label: 'Tipo de Pessoa', required: false },
  { id: 'email', label: 'Email', required: false },
  { id: 'telefone', label: 'Telefone', required: false },
  { id: 'parceiro', label: 'Parceiro Responsável', required: false },
  { id: 'periodo', label: 'Período', required: false },
  { id: 'data_cadastro', label: 'Data de Cadastro', required: false },
  { id: 'status', label: 'Status do Serviço', required: false },
  { id: 'sub_status', label: 'Subtítulo do Status', required: false },
  { id: 'baixa_items', label: 'Órgãos de Baixa (detalhado)', required: false },
  { id: 'conclusao_texto', label: 'Texto de Conclusão', required: false },
  { id: 'payment_status', label: 'Status de Pagamento', required: false },
];

export const ExportExcelModal = ({ onClose, periods = [] }) => {
  const [selectedColumns, setSelectedColumns] = useState(
    EXPORT_COLUMNS.filter(col => col.required).map(col => col.id)
  );
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('all');
  const [exporting, setExporting] = useState(false);

  const handleToggleColumn = (columnId) => {
    const column = EXPORT_COLUMNS.find(c => c.id === columnId);
    if (column?.required) return;

    setSelectedColumns(prev =>
      prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  const handleSelectAll = () => {
    setSelectedColumns(EXPORT_COLUMNS.map(col => col.id));
  };

  const handleSelectRequired = () => {
    setSelectedColumns(EXPORT_COLUMNS.filter(col => col.required).map(col => col.id));
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('columns', selectedColumns.join(','));
      if (selectedPeriod !== 'all') {
        params.append('period_id', selectedPeriod);
      }
      if (selectedPaymentStatus !== 'all') {
        params.append('payment_status', selectedPaymentStatus);
      }

      const response = await axios.get(
        `${API}/clients/export/excel?${params.toString()}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'clientes.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Exportação concluída com sucesso!');
      onClose();
    } catch (error) {
      console.error('Export error:', error);
      const errorMessage = error.response?.data?.detail || 'Erro ao exportar dados';
      toast.error(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  const optionalColumns = EXPORT_COLUMNS.filter(col => !col.required);
  const allSelected = selectedColumns.length === EXPORT_COLUMNS.length;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto" data-testid="export-excel-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="export-modal-title">
            <FileSpreadsheet className="w-5 h-5 text-primary" />
            Exportar para Excel
          </DialogTitle>
          <DialogDescription>
            Selecione as informações e filtros para a planilha
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Filters Section */}
          <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
            <p className="text-sm font-medium flex items-center gap-2">
              <Filter className="w-4 h-4" /> Filtros (Opcional)
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Período</Label>
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                  <SelectTrigger data-testid="export-period-filter">
                    <SelectValue placeholder="Todos os períodos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os períodos</SelectItem>
                    {periods.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Status de Pagamento</Label>
                <Select value={selectedPaymentStatus} onValueChange={setSelectedPaymentStatus}>
                  <SelectTrigger data-testid="export-payment-status-filter">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="pago">Pago</SelectItem>
                    <SelectItem value="processando">Processando</SelectItem>
                    <SelectItem value="nao_pago">Não Pago</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
              disabled={allSelected}
              data-testid="select-all-columns"
            >
              Selecionar Todos
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSelectRequired}
              data-testid="select-required-only"
            >
              Apenas Obrigatórios
            </Button>
          </div>

          {/* Required Columns */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Lock className="w-3 h-3" /> Campos Obrigatórios
            </p>
            <div className="grid grid-cols-2 gap-2">
              {EXPORT_COLUMNS.filter(col => col.required).map(column => (
                <div key={column.id} className="flex items-center space-x-2 bg-muted/50 p-2 rounded">
                  <Checkbox
                    id={column.id}
                    checked={true}
                    disabled={true}
                    data-testid={`column-${column.id}`}
                  />
                  <Label htmlFor={column.id} className="text-sm text-muted-foreground">
                    {column.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Optional Columns */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Campos Opcionais</p>
            <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
              {optionalColumns.map(column => (
                <div
                  key={column.id}
                  className={`flex items-center space-x-2 p-2 rounded cursor-pointer transition-colors ${
                    selectedColumns.includes(column.id)
                      ? 'bg-primary/10 border border-primary/30'
                      : 'bg-muted/30 hover:bg-muted/50'
                  }`}
                  onClick={() => handleToggleColumn(column.id)}
                >
                  <Checkbox
                    id={column.id}
                    checked={selectedColumns.includes(column.id)}
                    onCheckedChange={() => handleToggleColumn(column.id)}
                    data-testid={`column-${column.id}`}
                  />
                  <Label htmlFor={column.id} className="text-sm cursor-pointer">
                    {column.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-muted/30 p-3 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{selectedColumns.length}</span> de {EXPORT_COLUMNS.length} campos selecionados
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={exporting}
            data-testid="export-cancel-button"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting}
            data-testid="export-confirm-button"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'Exportando...' : 'Exportar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportExcelModal;
